import { sanitizeRegionCode } from '@yembo/yemblocks-core/dist/_util';
import { JobPlace } from 'src/types';

type GoogleAddressPlace = google.maps.places.Place;

export const parseFormattedAddressByCountry = (
  googlePlaceAddressComponents: GoogleAddressPlace,
  googleAddressName?: string
) => {
  let countryShortName = '';

  const addressComponentByType = (googlePlaceAddressComponents.addressComponents ?? []).reduce<Record<string, string>>(
    (acc, { types, shortText, longText }) => {
      types.forEach((type) => {
        if (type === 'country') countryShortName = shortText ?? '';

        if (type === 'route') {
          acc[type] = shortText ?? '';
          acc.route_long_name = longText ?? '';
        } else if (type === 'administrative_area_level_1') {
          acc[type] = shortText ?? '';
        } else {
          acc[type] = longText ?? '';
        }
      });

      return acc;
    },
    {}
  );

  return getAddressFields(addressComponentByType, countryShortName, googleAddressName);
};

type AddressFields = Pick<JobPlace, 'country' | 'streetAddress' | 'addressLine2' | 'city' | 'postalCode'> &
  Partial<Pick<JobPlace, 'district' | 'subdistrict' | 'state' | 'province' | 'prefecture'>> & {
    postalCodeName: string;
  };

export const getAddressFields = (
  {
    country = '',
    street_number = '',
    premise = '',
    subpremise = '',
    route = '',
    route_long_name = '',
    postal_town = '',
    locality = '',
    neighborhood = '',
    sublocality = '',
    sublocality_level_1 = '',
    sublocality_level_2 = '',
    sublocality_level_3 = '',
    sublocality_level_4 = '',
    administrative_area_level_1 = '',
    administrative_area_level_2 = '',
    administrative_area_level_3 = '',
    postal_code = '',
  },
  countryShortName: string,
  googleAddressName?: string
): AddressFields => {
  let streetAddress = '';

  if (
    googleAddressName &&
    (route || route_long_name) &&
    street_number &&
    googleAddressName.includes(street_number) &&
    (googleAddressName.includes(route_long_name) || googleAddressName.includes(route))
  ) {
    streetAddress = googleAddressName;
  }

  const mapping: Record<string, AddressFields> = {
    US: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      state: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'zipCode',
    },
    // ex: 2275 Bayview Ave, Toronto, ON M4N 3M6, Canada
    CA: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality,
      province: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 5 Rue de Thorigny, 75003 Paris, France
    FR: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 20 Upper Ground, South Bank, London SE1 9PD, United Kingdom
    UK: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        postal_town ||
        locality ||
        sublocality ||
        sublocality_level_1 ||
        administrative_area_level_3 ||
        administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Nymphenburger Str. 136, 80636 München, Germany
    DE: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex:; 295 Woodville Rd, Guildford NSW 2161, Australia
    AU: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      state: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postcode',
    },
    // ex: 6/12 Saint Martins Lane, Grafton, Auckland 1010, New Zealand
    NZ: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postcode',
    },
    // ex: 19 Yung Ho Rd, Singapore 618592
    SG: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality || sublocality || sublocality_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // Stadhouderskade 78, 1072 AE Amsterdam, Netherlands
    NL: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: China, Dongcheng, Dong Dan, 33 E Chang-an Ave
    CN: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      district: sublocality_level_1 || sublocality,
      city: locality || sublocality_level_1 || sublocality || administrative_area_level_1,
      province: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 1 Tsing Yi Rd, Tsing Yi, Hong Kong
    HK: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: neighborhood || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 626, Lal Bahadur Shastri Marg, Friends Colony, Colony Hallow Pul, Kurla West, Mumbai, Maharashtra 400070, India
    IN: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality,
      state: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Jl. Ungaran No.21, RT.6/RW.1, Guntur, Jakarta, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12970, Indonesia
    ID: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2 || administrative_area_level_3,
      province: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 2 Chome-2-2 Yoshino, Fukushima Ward, Osaka, 553-0006, Japan
    JP: {
      country,
      streetAddress: streetAddress || `${sublocality_level_3}-${sublocality_level_4}-${premise} ${sublocality_level_2}`,
      addressLine2: '',
      city: locality || administrative_area_level_1,
      prefecture: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 633-6 Dohwa-dong, Nam-gu, Incheon, South Korea
    KR: {
      country,
      streetAddress: streetAddress || `${premise} ${sublocality_level_4}, ${sublocality_level_2}`,
      addressLine2: '',
      district: sublocality_level_1,
      city: locality,
      province: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 3, Jalan Stesen Sentral, Kuala Lumpur Sentral, 50470 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur, Malaysia
    MY: {
      country,
      streetAddress: streetAddress || `${street_number}, ${route}`,
      addressLine2: '',
      district: sublocality_level_1,
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Baga Toiruu 6, Th Khoroo Ulaanbaatar, 14201, Mongolia
    MN: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      subdistrict: sublocality_level_1,
      district: administrative_area_level_2,
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Hotel Zone 1, Shwe Kyar Pin St, Dekkinathiri Township, Naypyitaw 12211, Myanmar (Burma)
    MM: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      subdistrict: sublocality_level_1,
      district: administrative_area_level_2,
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 1171 Guerrero St, Ermita, Manila, 1000 Metro Manila, Philippines
    PH: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      district: sublocality_level_1,
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 100 Rose Ave, Nairobi, Kenya
    KE: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 247 Scheiding St, Pretoria Central, Pretoria, 0002, South Africa
    ZA: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: No. 38, Section 3, Chongqing South Road, Zhongzheng District, Taipei City, Taiwan 100
    TW: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      district: administrative_area_level_3,
      city: administrative_area_level_1 || locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 566 Phloen Chit Rd, Khwaeng Lumphini, Pathum Wan District, Bangkok 10330, Thailand
    TH: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      subdistrict: sublocality_level_2,
      district: sublocality_level_1,
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 28 Charoen Krung Rd, Wat Phraya Krai, Bang Kho Laem, Bangkok 10120, Thailand
    VN: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      district: administrative_area_level_1,
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Sheikh Zayed Road Exit 41، Al Thanya Street - United Arab Emirates
    AE: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: 12 Esplanade, 9227 Diekirch, Luxembourg
    LU: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Rue Berckmans 128, 1060 Bruxelles, Belgium
    BE: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Dostyk Street 16, Nur-Sultan 010016, Kazakhstan
    KZ: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Ulitsa 10-Letiya Oktyabrya, 11, Moscow, Russia, 119048
    RU: {
      country,
      streetAddress: streetAddress || `${route}, ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: bulevard 'Knyaginya Maria Luiza' 131 Blvd, 1202 Sofia Center, Sofia, Bulgaria
    BG: {
      country,
      streetAddress: streetAddress || `${route}, ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Senovážné nám. 987/15, 110 00 Nové Město, Czechia
    CZ: {
      country,
      streetAddress: streetAddress || `${subpremise} ${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2 || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Budapest, Széchenyi István tér 5-6, 1051 Hungary
    HU: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Strada Tulcea 17, Timișoara 300351, Romania
    RO: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Marszałkowska 115, 00-102 Warszawa, Poland
    PL: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Trieda SNP 2286/85, 040 11 Košice, Slovakia
    SK: {
      country,
      streetAddress: streetAddress || `${subpremise || ''} ${route} ${premise || ''} ${street_number || ''}`,
      addressLine2: '',
      city: sublocality_level_1 || administrative_area_level_2 || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Sackstraße 3-5, 8010 Graz, Austria
    AT: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Milentija Popovića 5, Beograd 11070, Serbia
    RS: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Predslavynska St, 35Д, Kyiv, Ukraine, 03150
    UA: {
      country,
      streetAddress: streetAddress || `${route}, ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Via Lupetta, 5, 20123 Milano MI, Italy
    IT: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_3,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Rua da Boavista 783, 4050-110 Porto, Portugal
    PT: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Praza do Obradoiro, 1, 15705 Santiago de Compostela, A Coruña, Spain
    ES: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Beethovenstrasse 21, 8002 Zürich, Switzerland
    CH: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || administrative_area_level_2 || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Palmfeltsvägen 19, 121 62 Johanneshov, Sweden
    SE: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || postal_town,
      postalCode: postal_code,
      postalCodeName: 'postcode',
    },
    // ex: Havnegade 20, 6700 Esbjerg, Denmark
    DK: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || postal_town,
      postalCode: postal_code,
      postalCodeName: 'postcode',
    },
    // ex: Unioninkatu 29, 00170 Helsinki, Finland
    FI: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: locality || postal_town,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // ex: Kirsten Flagstads Plass 1, 0150 Oslo, Norway
    NO: {
      country,
      streetAddress: streetAddress || `${route} ${street_number}`,
      addressLine2: '',
      city: postal_town || locality || administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
    // default if country is not supported
    default: {
      country,
      streetAddress: streetAddress || `${street_number} ${route}`,
      addressLine2: '',
      city:
        locality || sublocality || sublocality_level_1 || administrative_area_level_3 || administrative_area_level_2,
      state: administrative_area_level_1,
      postalCode: postal_code,
      postalCodeName: 'postalCode',
    },
  };
  const sanitizedRegionCode = countryShortName ? sanitizeRegionCode(countryShortName).toUpperCase() : '';

  const addressFields = mapping[sanitizedRegionCode] || mapping.default;
  addressFields.streetAddress = addressFields.streetAddress.trim();

  return addressFields;
};
